import { assign, createMachine } from "xstate";
import { fetchMember } from "@nju/data-access-portal-member";
import { getMsisdnNumbersForReservation } from "@nju/data-access-portal-msisdn";
import {
  createOrchestrator,
  fetchOrchestrators,
  IChangeMsisdnOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import {
  cancelOrder,
  commitOrder,
  createChangeMsisdnOrder,
} from "@nju/data-access-portal-order";
import { fetchOrderProducts } from "@nju/data-access-portal-product";
import { BasicError, ContractError } from "@nju/result";

export interface INumbers {
  id?: string;
  value: string;
  msisdnToken?: string;
}

interface IContext {
  numbers: INumbers[];
  errorMessage: string | undefined;
  orchestrator?: IChangeMsisdnOrchestrator;
  orchestratorId: string;
  selectedNumber: string;
  deleteAfterDate: string | undefined;
  chooseNewNumber: string;
  userId: string | undefined;
}

type IEvent =
  | { type: "DONE"; orchestrator: IChangeMsisdnOrchestrator }
  | { type: "ERROR" }
  | { type: "SUBMIT" }
  | { type: "SUCCESS" }
  | { type: "FETCH_NEW_NUMBERS" }
  | { type: "FETCH_ERROR" }
  | {
      type: "SET_ORCHESTRATOR_DATA";
      orchestratorId: string;
    }
  | { type: "SET_SELECTED_NUMBER"; value: string }
  | { type: "NUMBERS"; numbers: INumbers[] }
  | { type: "CHANGE_NUMBER" }
  | { type: "COMMIT_ORDER" }
  | { type: "COMMIT_ORDER_SUCCESS" }
  | { type: "CANCEL_ORDER_SUCCESS" }
  | { type: "CANCEL_ORDER" }
  | {
      type: "ORDER_SUMMARY";
      chooseNewNumber: string;
      orchestratorId: string;
    }
  | { type: "ORDER_COMPLETED"; deleteAfterDate: string | undefined }
  | { type: "ANOTHER_ORDER_EXISTS" };

export const mySimCardChangeNumberMachine = createMachine(
  {
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    initial: "init",
    states: {
      init: {
        invoke: {
          src: "fetchOrchestrator",
        },
        on: {
          SET_ORCHESTRATOR_DATA: {
            actions: "setInitialData",
            target: "#ready",
          },
          ORDER_SUMMARY: {
            actions: "setInitialData",
            target: "#confirmChangeNumber",
          },
          ORDER_COMPLETED: {
            actions: "setInitialData",
            target: "#orderCompleted",
          },
          ERROR: {
            target: "#error",
          },
        },
      },
      ready: {
        id: "ready",
        on: {
          CHANGE_NUMBER: {
            target: "changeNumber",
          },
        },
      },
      changeNumber: {
        id: "changeNumber",
        initial: "loading",
        states: {
          loading: {
            entry: "clearSelectedNumber",
            invoke: {
              src: "loading",
            },
            on: {
              NUMBERS: {
                actions: "setNumbers",
                target: "ready",
              },
              FETCH_ERROR: {
                target: "#noDataScreen",
              },
            },
          },
          ready: {
            after: {
              300_000: {
                target: "phoneNumbersExpired",
              },
            },
            on: {
              SET_SELECTED_NUMBER: {
                actions: "setSelectedNumber",
              },
              FETCH_NEW_NUMBERS: {
                target: "loading",
              },
              SUBMIT: [
                {
                  cond: "checkIfNumberSelected",
                  target: "newOrder",
                },
                {
                  actions: "setErrorMessage",
                },
              ],
            },
          },
          phoneNumbersExpired: {
            on: {
              SET_SELECTED_NUMBER: {
                actions: "setSelectedNumber",
              },
              FETCH_NEW_NUMBERS: {
                target: "loading",
              },
              SUBMIT: [
                {
                  cond: "checkIfNumberSelected",
                  target: "#expiredScreen",
                },
                {
                  actions: "setErrorMessage",
                },
              ],
            },
          },
          newOrder: {
            invoke: {
              src: "createOrder",
            },
            on: {
              DONE: {
                actions: "setInitialData",
                target: "#confirmChangeNumber",
              },
              ERROR: "#error",
            },
          },
        },
      },
      expiredScreen: {
        id: "expiredScreen",
        on: {
          FETCH_NEW_NUMBERS: {
            target: "changeNumber",
          },
        },
      },
      noDataScreen: {
        id: "noDataScreen",
        on: {
          FETCH_NEW_NUMBERS: {
            target: "changeNumber",
          },
        },
      },
      confirmChangeNumber: {
        id: "confirmChangeNumber",
        initial: "ready",
        states: {
          ready: {
            on: {
              COMMIT_ORDER: {
                target: "#commitOrder",
              },
              CANCEL_ORDER: {
                target: "cancelOrder",
              },
            },
          },
          commitOrder: {
            id: "commitOrder",
            invoke: {
              src: "commitOrder",
            },
            on: {
              COMMIT_ORDER_SUCCESS: {
                target: "#success",
              },
              ERROR: {
                target: "#error",
              },
            },
          },
          cancelOrder: {
            invoke: {
              src: "cancelOrder",
            },
            on: {
              CANCEL_ORDER_SUCCESS: {
                actions: "onCancelOrderSuccess",
              },
              ERROR: {
                target: "#error",
              },
            },
          },
        },
      },
      orderCompleted: {
        id: "orderCompleted",
        type: "final",
      },
      error: {
        id: "error",
        type: "final",
      },
      success: {
        id: "success",
        type: "final",
      },
    },
  },
  {
    actions: {
      setSelectedNumber: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_SELECTED_NUMBER") {
          return {
            selectedNumber: event.value,
            errorMessage: undefined,
          };
        }

        return context;
      }),
      setNumbers: assign<IContext, IEvent>((context, event) => {
        if (event.type === "NUMBERS") {
          // by default selected middle number from the list
          const defaultSelectedNumber =
            event.numbers.length > 2
              ? event.numbers[event.numbers.length / 2 - 1]
              : event.numbers[0];

          return {
            numbers: event.numbers,
            selectedNumber: defaultSelectedNumber.msisdnToken,
          };
        }

        return context;
      }),
      setErrorMessage: assign<IContext, IEvent>((_, __) => {
        return {
          errorMessage: "wybierz numer, aby przejść dalej",
        };
      }),
      clearSelectedNumber: assign<IContext, IEvent>((_, __) => {
        return {
          selectedNumber: undefined,
        };
      }),
      setInitialData: assign<IContext, IEvent>((context, event) => {
        if (event.type === "DONE") {
          return {
            chooseNewNumber: event.orchestrator.verifiedMsisdn,
          };
        }

        if (event.type === "ORDER_SUMMARY") {
          return {
            chooseNewNumber: event.chooseNewNumber,
            orchestratorId: event.orchestratorId,
          };
        }

        if (event.type === "ORDER_COMPLETED") {
          return {
            deleteAfterDate: event.deleteAfterDate,
          };
        }

        if (event.type === "SET_ORCHESTRATOR_DATA") {
          return {
            orchestratorId: event.orchestratorId,
          };
        }

        return context;
      }),
    },
    services: {
      loading: () => {
        return async (callback) => {
          const result = await getMsisdnNumbersForReservation();

          if (result.isError || result.value.numbers.length === 0) {
            return callback({ type: "FETCH_ERROR" });
          }

          return callback({
            type: "NUMBERS",
            numbers: result.value.numbers,
          });
        };
      },
      fetchOrchestrator: ({ userId }) => {
        return async (callback) => {
          try {
            const products = userId
              ? (await fetchOrderProducts(userId)).unwrap()
              : (await fetchOrderProducts()).unwrap();
            const productId = products.mobileProducts.mobileAccess?.id;

            if (!productId) {
              throw new BasicError({
                message: "Missing mobile productId",
              });
            }

            await createOrchestrator("NJU_CHANGE_MSISDN", {
              product: { id: productId },
            });

            const { changeMsisdn } = userId
              ? (await fetchMember({ userId })).unwrap()
              : (await fetchOrchestrators()).unwrap();

            if (!changeMsisdn) {
              throw new BasicError({
                message: "Missing changeMsisdn orchestrator",
              });
            }

            if (changeMsisdn.state === "ORDER_SUMMARY") {
              if (changeMsisdn.verifiedMsisdn === undefined) {
                return callback({ type: "ERROR" });
              }

              return callback({
                type: "ORDER_SUMMARY",
                chooseNewNumber: changeMsisdn.verifiedMsisdn,
                orchestratorId: changeMsisdn.id,
              });
            }

            if (changeMsisdn.state === "COMPLETED") {
              return callback({
                type: "ORDER_COMPLETED",
                deleteAfterDate: changeMsisdn.deleteAfterDate,
              });
            }

            return callback({
              type: "SET_ORCHESTRATOR_DATA",
              orchestratorId: changeMsisdn.id,
            });
          } catch {
            callback({ type: "ERROR" });
          }
        };
      },
      createOrder: ({ selectedNumber, orchestratorId }) => {
        return async (callback) => {
          try {
            if (selectedNumber === null) {
              throw new ContractError({
                message: "`selectedNumber` cannot be null",
              });
            }

            if (!orchestratorId) {
              throw new ContractError({
                message: "`orchestrator.id` is missing",
              });
            }

            const result = await createChangeMsisdnOrder({
              mode: "changeMsisdn",
              orchestratorId,
              msisdnToken: selectedNumber,
            });

            if (
              result.isError &&
              result.err.code === "ANOTHER_ORDER_IN_PROGRESS"
            ) {
              return callback({ type: "ANOTHER_ORDER_EXISTS" });
            }

            if (result.isError) {
              return callback({ type: "ERROR" });
            }

            return callback({
              type: "DONE",
              orchestrator: result.value,
            });
          } catch {
            callback({
              type: "ERROR",
            });
          }
        };
      },
      commitOrder: ({ orchestratorId }) => {
        return async (callback) => {
          const result = await commitOrder({
            mode: "changeMsisdn",
            consent: [],
            orchestratorId,
          });

          if (result.isError) {
            return callback({ type: "ERROR" });
          }

          return callback({ type: "COMMIT_ORDER_SUCCESS" });
        };
      },
      cancelOrder: ({ orchestratorId }) => {
        return async (callback) => {
          const result = await cancelOrder({
            reason: "userRequest",
            orchestratorId,
            mode: "changeMsisdn",
          });

          if (result.isError) {
            return callback({ type: "ERROR" });
          }

          return callback({ type: "CANCEL_ORDER_SUCCESS" });
        };
      },
    },
    guards: {
      checkIfNumberSelected: ({ selectedNumber }) => {
        return selectedNumber !== undefined;
      },
    },
  },
);
